import {
  EXPENSE_TYPE,
  INCOME_TYPE,
  type Category,
} from "../../common/commonConstant";
import useDeviceModeFlg from "../../common/useDeviceModeFlg";
import Spinner from "../../common/Spinner";
import { useMaster } from "../../common/MasterContext";
import { getExpenseRatioLevel } from "../../common/expenseRatioLevel";
import type { ItemRatio } from "./bop020w010";

interface BOP020W010TemplateProps {
  type: Category;
  categoryLarge: string;
  categorySmall: string;
  amount: string;
  date: string;
  memo: string;
  keihiFlg: 0 | 1;
  mikakuteiFlg: 0 | 1;
  keepInputFlg: boolean;
  isEditing: boolean;
  isLoading: boolean;
  expenseRatio: number | null;
  itemRatios: ItemRatio[];
  submitButtonLabel: string;
  onTypeChange: (newType: Category) => void;
  onCategoryLargeChange: (value: string) => void;
  onCategorySmallChange: (value: string) => void;
  onAmountChange: (value: string) => void;
  onDateChange: (value: string) => void;
  onMemoChange: (value: string) => void;
  onKeihiFlgChange: (checked: boolean) => void;
  onMikakuteiFlgChange: (checked: boolean) => void;
  onKeepInputFlgChange: (checked: boolean) => void;
  onClear: () => void;
  onBack: () => void;
  onDelete: () => void;
  onSubmit: () => void;
  onExpenseRatioClick: () => void;
}

const BOP020W010Template = ({
  type,
  categoryLarge,
  categorySmall,
  amount,
  date,
  memo,
  keihiFlg,
  mikakuteiFlg,
  keepInputFlg,
  isEditing,
  isLoading,
  expenseRatio,
  itemRatios,
  submitButtonLabel,
  onTypeChange,
  onCategoryLargeChange,
  onCategorySmallChange,
  onAmountChange,
  onDateChange,
  onMemoChange,
  onKeihiFlgChange,
  onMikakuteiFlgChange,
  onKeepInputFlgChange,
  onClear,
  onBack,
  onDelete,
  onSubmit,
  onExpenseRatioClick,
}: BOP020W010TemplateProps) => {
  const deviceModeFlg = useDeviceModeFlg();
  const { categoryLargeList, categorySmallList } = useMaster();

  const handleDateInputClick = (e: React.MouseEvent<HTMLInputElement>) => {
    e.currentTarget.showPicker?.();
  };

  const handleDateInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Tab") return;
    e.preventDefault();
  };

  return (
    <div
      className={`bop020w010-container ${
        deviceModeFlg ? "deviceMode-sp" : "deviceMode-pc"
      }`}
    >
      <div className="bop020w010-form">
        {isLoading && (
          <div className="bop020w010-loading-overlay">
            <Spinner label="処理中..." />
          </div>
        )}
        <div className="screen-title">収支登録画面</div>
        <button
          type="button"
          className={`expense-ratio expense-ratio-link expense-ratio-${getExpenseRatioLevel(expenseRatio)}`}
          onClick={onExpenseRatioClick}
          title="月次集計画面で今月の集計を表示"
        >
          今月の支出割合：
          <span className="expense-ratio-value">
            {expenseRatio === null ? "-" : `${expenseRatio.toFixed(1)}%`}
          </span>
        </button>
        {itemRatios.map((item) => (
          <div
            key={item.key}
            className={`expense-ratio expense-ratio-item expense-ratio-${getExpenseRatioLevel(item.ratio)}`}
          >
            今月の{item.label}の支出割合：
            <span className="expense-ratio-value">
              {item.ratio === null ? "-" : `${item.ratio.toFixed(1)}%`}
            </span>
          </div>
        ))}

        <div className="form-columns">
          <div className="form-column">
            <div className="form-row">
              <label className="form-label required-label">収支</label>

              <div className="radio-group">
                <label className="radio-item">
                  <input
                    type="radio"
                    name="type"
                    checked={type === INCOME_TYPE}
                    onChange={() => onTypeChange(INCOME_TYPE)}
                  />
                  収入
                </label>

                <label className="radio-item">
                  <input
                    type="radio"
                    name="type"
                    checked={type === EXPENSE_TYPE}
                    onChange={() => onTypeChange(EXPENSE_TYPE)}
                  />
                  支出
                </label>
              </div>
            </div>

            <div className="form-row">
              <label className="form-label required-label">項目（大）</label>
              <select
                value={categoryLarge}
                onChange={(e) => onCategoryLargeChange(e.target.value)}
              >
                <option value="">選択してください</option>
                {categoryLargeList
                  .filter((item) => item.type === type)
                  .map((item) => (
                    <option key={item.label} value={item.category}>
                      {item.label}
                    </option>
                  ))}
              </select>
            </div>

            <div className="form-row">
              <label className="form-label required-label">項目（小）</label>
              <select
                value={categorySmall}
                onChange={(e) => onCategorySmallChange(e.target.value)}
              >
                <option value="">選択してください</option>
                {categorySmallList
                  .filter((item) => item.category === categoryLarge)
                  .map((item) => (
                    <option key={item.label} value={item.label}>
                      {item.label}
                    </option>
                  ))}
              </select>
            </div>

            <div className="form-row">
              <label className="form-label required-label">金額（円）</label>
              <input
                type="number"
                value={amount}
                onChange={(e) => onAmountChange(e.target.value)}
              />
            </div>
          </div>

          <div className="form-column">
            <div className="form-row">
              <label className="form-label required-label">日付</label>
              <input
                type="date"
                value={date}
                onChange={(e) => onDateChange(e.target.value)}
                onClick={handleDateInputClick}
                onKeyDown={handleDateInputKeyDown}
              />
            </div>

            <div className="form-row">
              <label className="form-label">備考</label>
              <textarea
                value={memo}
                onChange={(e) => onMemoChange(e.target.value)}
                className="memo-area"
              />
            </div>

            <div className="form-row">
              <label className="form-label">経費</label>
              <label className="toggle-switch" aria-label="経費トグル">
                <input
                  type="checkbox"
                  checked={keihiFlg === 1}
                  onChange={(e) => onKeihiFlgChange(e.target.checked)}
                />
                <span className="toggle-slider" />
              </label>
            </div>

            <div className="form-row">
              <label className="form-label">未確定</label>
              <label className="toggle-switch" aria-label="未確定トグル">
                <input
                  type="checkbox"
                  checked={mikakuteiFlg === 1}
                  onChange={(e) => onMikakuteiFlgChange(e.target.checked)}
                />
                <span className="toggle-slider" />
              </label>
            </div>

            <div className="form-row">
              <label className="form-label">入力内容保持</label>
              <label className="toggle-switch" aria-label="入力内容保持トグル">
                <input
                  type="checkbox"
                  checked={keepInputFlg}
                  onChange={(e) => onKeepInputFlgChange(e.target.checked)}
                />
                <span className="toggle-slider" />
              </label>
            </div>
          </div>
        </div>

        <div className="action-row">
          <div className="action-group action-group-left">
            <button type="button" className="clear-button" onClick={onBack}>
              戻る
            </button>
            <button
              className="clear-button"
              onClick={onClear}
              disabled={isLoading}
            >
              クリア
            </button>
            <button
              className="delete-button"
              onClick={onDelete}
              disabled={!isEditing || isLoading}
            >
              削除
            </button>
          </div>
          <div className="action-group action-group-right">
            <button
              className="register-button"
              onClick={onSubmit}
              disabled={isLoading}
            >
              {submitButtonLabel}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BOP020W010Template;
